import React, { useState } from 'react';
import { buildLanguageMap } from '../utils/languageConfig';
import './HomeCardTranslationPreview.css';

const HomeCardTranslationPreview = ({
  translatedData,
  sourceLanguage = 'en',
  supportedLanguages,
  onClose,
  onSave,
  loading = false
}) => {
  const [currentLanguage, setCurrentLanguage] = useState(sourceLanguage);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [localTranslatedData, setLocalTranslatedData] = useState(translatedData);

  // Build language map from supportedLanguages prop (ISO codes), or fall back to translatedData keys
  const languages = supportedLanguages
    ? (typeof supportedLanguages === 'object' && !Array.isArray(supportedLanguages)
        ? supportedLanguages
        : buildLanguageMap(supportedLanguages))
    : buildLanguageMap(Object.keys(translatedData || {}));

  const availableLanguages = Object.keys(languages);
  const currentData = localTranslatedData?.[currentLanguage] || {};

  // Handle inline editing
  const startEditing = (fieldKey, currentValue) => {
    setEditingField(`${currentLanguage}-${fieldKey}`);
    setEditValue(currentValue || '');
  };

  const saveEdit = () => {
    const [lang, fieldKey] = editingField.split('-');
    const updatedData = {
      ...localTranslatedData,
      [lang]: {
        ...localTranslatedData[lang],
        [fieldKey]: editValue
      }
    };
    setLocalTranslatedData(updatedData);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(localTranslatedData);
    }
  };

  const renderField = (fieldKey, label, value) => {
    const isEditing = editingField === `${currentLanguage}-${fieldKey}`;

    return (
      <div className="translation-field">
        <label className="field-label">{label}</label>
        {isEditing ? (
          <div className="edit-mode">
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="edit-textarea"
              rows={fieldKey === 'text_content' ? 4 : 2}
            />
            <div className="edit-actions">
              <button
                className="btn-save-edit"
                onClick={saveEdit}
              >
                Save
              </button>
              <button
                className="btn-cancel-edit"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="view-mode">
            <div
              className="field-content"
              dir={languages[currentLanguage]?.direction || 'ltr'}
            >
              {value || 'No translation available'}
            </div>
            <button
              className="btn-edit-field"
              onClick={() => startEditing(fieldKey, value)}
            >
              ✏️ Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="home-card-translation-preview">
      <div className="preview-header">
        <h3>🌐 Translation Preview</h3>
        <div className="language-selector">
          <label>View Language:</label>
          <select
            value={currentLanguage}
            onChange={(e) => setCurrentLanguage(e.target.value)}
          >
            {availableLanguages.map(lang => (
              <option key={lang} value={lang}>
                {languages[lang].flag} {languages[lang].name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="preview-content">
        <div className="language-info">
          <span className="current-language">
            {languages[currentLanguage]?.flag} {languages[currentLanguage]?.name}
          </span>
          <span className="language-count">
            {availableLanguages.indexOf(currentLanguage) + 1} of {availableLanguages.length}
          </span>
        </div>

        <div className="translation-fields">
          {renderField('title', 'Card Title', currentData.title)}
          {renderField('text_content', 'Card Description', currentData.text_content)}
        </div>
      </div>

      <div className="preview-actions" style={{ display: 'flex', visibility: 'visible' }}>
        <button
          className="btn-cancel"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className="btn-save"
          onClick={handleSave}
          disabled={loading}
          style={{ display: 'flex', visibility: 'visible' }}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            <>
              💾 Save All Translations
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default HomeCardTranslationPreview;
